import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@context/analytics/analytics.context';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import clsx from 'clsx';
import { accountSettingsSaveEventPayload } from '@tracking/account-settings/account-settings-tracking';
import styles from './personal-information-edit.module.scss';

const BIO_MAX_LENGTH = 400;

type PersonalInformationFormValues = {
  firstname: string;
  lastname: string;
  username: string;
  bio: string;
};

type PersonalInformationEditProps = {
  firstname: string;
  lastname?: string;
  username?: string;
  bio?: string;
  isPending: boolean;
  onSave: (data: PersonalInformationFormValues) => void;
  apiError?: string | null;
  onErrorReset?: () => void;
};

const PersonalInformationEdit: React.FC<PersonalInformationEditProps> = ({
  firstname,
  lastname,
  username,
  bio,
  isPending,
  onSave,
  apiError,
  onErrorReset,
}) => {
  const { t } = useTranslation('account-settings');
  const { sendEvent } = useAnalytics();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<PersonalInformationFormValues>({
    mode: 'onChange',
    defaultValues: {
      firstname: firstname ?? '',
      lastname: lastname ?? '',
      username: username ?? '',
      bio: bio ?? '',
    },
  });

  const bioValue = watch('bio');

  useEffect(() => {
    if (!apiError) {
      return;
    }
    const subscription = watch(() => onErrorReset?.());
    return () => subscription.unsubscribe();
  }, [apiError, watch, onErrorReset]);

  const onSubmit = (data: PersonalInformationFormValues) => {
    sendEvent({ type: 'event', payload: { ...accountSettingsSaveEventPayload, property: 'personal_information' } });
    onSave(data);
  };

  return (
    <form
      className={styles.personalInformationEdit}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-cy="personal-information-form"
    >
      <div className={styles.personalInformationEdit__row}>
        <div className={styles.personalInformationEdit__field}>
          <label
            htmlFor="firstname"
            className={styles.personalInformationEdit__label}
          >
            {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.FIRST_NAME')}
          </label>
          <input
            id="firstname"
            type="text"
            className={clsx('vc-inputText', errors.firstname && styles.personalInformationEdit__inputError)}
            data-cy="account-settings-personal-information-firstname-input"
            {...register('firstname', { required: true })}
          />
          {errors.firstname && (
            <span
              role="alert"
              className={styles.personalInformationEdit__error}
            >
              <Icon
                name="warning-default"
                ariaHidden
              />
              {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.REQUIRED_ERROR')}
            </span>
          )}
        </div>

        <div className={styles.personalInformationEdit__field}>
          <label
            htmlFor="lastname"
            className={styles.personalInformationEdit__label}
          >
            {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.LAST_NAME')}
          </label>
          <input
            id="lastname"
            type="text"
            className={clsx('vc-inputText', errors.lastname && styles.personalInformationEdit__inputError)}
            data-cy="account-settings-personal-information-lastname-input"
            {...register('lastname', { required: true })}
          />
          {errors.lastname && (
            <span
              role="alert"
              className={styles.personalInformationEdit__error}
            >
              <Icon
                name="warning-default"
                ariaHidden
              />
              {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.REQUIRED_ERROR')}
            </span>
          )}
        </div>
      </div>

      <div className={styles.personalInformationEdit__row}>
        <div className={styles.personalInformationEdit__field}>
          <label
            htmlFor="username"
            className={styles.personalInformationEdit__label}
          >
            {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.USERNAME')}
          </label>
          <input
            id="username"
            type="text"
            className={clsx('vc-inputText', errors.username && styles.personalInformationEdit__inputError)}
            data-cy="account-settings-personal-information-username-input"
            {...register('username', { required: true })}
          />
          {errors.username && (
            <span
              role="alert"
              className={styles.personalInformationEdit__error}
            >
              <Icon
                name="warning-default"
                ariaHidden
              />
              {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.REQUIRED_ERROR')}
            </span>
          )}
        </div>
      </div>

      <div className={styles.personalInformationEdit__row}>
        <div className={styles.personalInformationEdit__field}>
          <label
            htmlFor="bio"
            className={styles.personalInformationEdit__label}
          >
            {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.BIO')}
          </label>
          <textarea
            id="bio"
            className={clsx('vc-inputText', styles.personalInformationEdit__textarea)}
            maxLength={BIO_MAX_LENGTH}
            placeholder={t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.BIO_PLACEHOLDER')}
            data-cy="account-settings-personal-information-bio-input"
            aria-describedby="bio-help bio-counter"
            {...register('bio', { maxLength: BIO_MAX_LENGTH })}
          />
          <span
            id="bio-help"
            className="vc-d-none"
          >
            {/* TODO: Translation */}
            Maximum {BIO_MAX_LENGTH} characters.
          </span>
          <span
            id="bio-counter"
            className={styles.personalInformationEdit__bioCounter}
            aria-live="polite"
          >
            {bioValue.length}/{BIO_MAX_LENGTH}
          </span>
        </div>
      </div>
      {apiError && (
        <p
          role="alert"
          className={styles.personalInformationEdit__globalError}
        >
          <Icon
            name="warning-default"
            ariaHidden
          />
          {apiError}
        </p>
      )}
      <div className={styles.personalInformationEdit__row}>
        <Button
          variant="primary"
          type="submit"
          size="large"
          disabled={!isDirty || !isValid || isPending}
          data-cy="account-settings-personal-information-save-button"
        >
          {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.SAVE')}
        </Button>
      </div>
    </form>
  );
};

export default PersonalInformationEdit;
